import { useState, useMemo } from "react";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { Input } from "components/ui/input";
import { ScrollArea } from "components/ui/scroll-area";
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

import { sortStoresByAvailability, type StoreOption } from "./store-order";

interface StoreMultiSelectProps {
  options: StoreOption[];
  selected: number[];
  onChange: (stores: number[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  availableSet?: Set<number>;
}

export function StoreMultiSelect({
  options,
  selected,
  onChange,
  isLoading,
  disabled,
  availableSet,
}: StoreMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const orderedOptions = useMemo(
    () => sortStoresByAvailability(options, availableSet),
    [options, availableSet]
  );

  const filtered = useMemo(() => {
    if (!search) return orderedOptions;
    const lower = search.toLowerCase();
    return orderedOptions.filter(
      (o) =>
        o.value.toLowerCase().includes(lower) ||
        o.label.toLowerCase().includes(lower),
    );
  }, [orderedOptions, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Quando a disponibilidade OFM é conhecida, lojas sem dado (Pendentes) não
  // podem ser selecionadas — só exportamos lojas com dados disponíveis.
  const isSelectable = (code: number) =>
    availableSet === undefined || availableSet.has(code);

  const toggleStore = (code: number) => {
    if (!isSelectable(code)) return;
    if (selectedSet.has(code)) {
      onChange(selected.filter((s) => s !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  const selectAll = () => {
    const allCodes = filtered.map((o) => Number(o.value)).filter(isSelectable);
    const merged = new Set([...selected, ...allCodes]);
    onChange(Array.from(merged));
  };

  const deselectAll = () => {
    const filteredCodes = new Set(filtered.map((o) => Number(o.value)));
    onChange(selected.filter((s) => !filteredCodes.has(s)));
  };

  const selectableFiltered = filtered.filter((o) =>
    isSelectable(Number(o.value)),
  );
  const allFilteredSelected =
    selectableFiltered.length > 0 &&
    selectableFiltered.every((o) => selectedSet.has(Number(o.value)));

  const buttonLabel =
    selected.length === 0
      ? "Selecionar lojas..."
      : `${selected.length} loja${selected.length > 1 ? "s" : ""}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={isLoading || disabled}
        >
          {isLoading ? "Carregando..." : buttonLabel}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Buscar loja..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="p-2 border-b flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={allFilteredSelected ? deselectAll : selectAll}
          >
            {allFilteredSelected ? "Desmarcar todos" : "Selecionar todos"}
          </Button>
        </div>
        <ScrollArea className="h-[200px]">
          <div className="p-2 flex flex-col gap-1">
            {filtered.map((option) => {
              const code = Number(option.value);
              const isAvailable = availableSet?.has(code);
              const pending = availableSet !== undefined && !isAvailable;
              return (
                <label
                  key={option.value}
                  title={
                    pending ? "Loja sem dados OFM para o período" : undefined
                  }
                  className={cn(
                    "flex items-center gap-2 px-2 py-1 rounded text-sm",
                    pending
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:bg-muted cursor-pointer",
                  )}
                >
                  <Checkbox
                    checked={selectedSet.has(code)}
                    onCheckedChange={() => toggleStore(code)}
                    disabled={pending}
                  />
                  <span className="flex-1">{option.label}</span>
                  {availableSet !== undefined && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        isAvailable
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {isAvailable ? "Disponível" : "Pendente"}
                    </span>
                  )}
                </label>
              );
            })}
            {filtered.length === 0 && (
              <span className="text-sm text-muted-foreground px-2">
                Nenhuma loja encontrada
              </span>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
